import { createStaticRanges, DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { useEffect, useRef, useState } from "react";
import { fr } from "date-fns/locale";
import {
  subDays,
  startOfMonth,
  endOfMonth,
  endOfDay,
  startOfDay,
} from "date-fns";

interface DateFiltersProps {
  label?: string;
  onChange?: (dates: { from: string; to: string }) => void;
  defaultStartDate?: string;
  defaultEndDate?: string;
}

export default function CalendarDateFilters({
  label,
  onChange,
  defaultStartDate,
  defaultEndDate,
}: DateFiltersProps) {
  const [showPicker, setShowPicker] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [range, setRange] = useState([
    {
      startDate: defaultStartDate ? new Date(defaultStartDate) : undefined,
      endDate: defaultEndDate ? new Date(defaultEndDate) : undefined,
      key: "selection",
    },
  ]);

  const formatDate = (date?: Date) =>
    date
      ? date.toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "—";

  const customStaticRanges = createStaticRanges([
    {
      label: "Aujourd'hui",
      range: () => ({
        startDate: startOfDay(new Date()),
        endDate: endOfDay(new Date()),
      }),
    },
    {
      label: "Hier",
      range: () => ({
        startDate: startOfDay(subDays(new Date(), 1)),
        endDate: endOfDay(subDays(new Date(), 1)),
      }),
    },
    {
      label: "7 derniers jours",
      range: () => ({
        startDate: startOfDay(subDays(new Date(), 6)),
        endDate: endOfDay(new Date()),
      }),
    },
    {
      label: "Ce mois-ci",
      range: () => ({
        startDate: startOfMonth(new Date()),
        endDate: endOfMonth(new Date()),
      }),
    },
    // {
    //   label: "Tout",
    //   range: () => ({
    //     startDate: new Date(2000, 0, 1),
    //     endDate: endOfDay(new Date()),
    //   }),
    // },
  ]);

  const handleSelect = (ranges: any) => {
    const { startDate, endDate } = ranges.selection;

    if (startDate && endDate) {
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);

      setRange([ranges.selection]);
      onChange?.({
        from: startDate.toISOString(),
        to: endOfDay.toISOString(),
      });
      setShowPicker(false);
    } else {
      setRange([ranges.selection]);
    }
  };

  const handleReset = () => {
    setRange([
      {
        startDate: undefined,
        endDate: undefined,
        key: "selection",
      },
    ]);
    onChange?.({ from: "", to: "" });
    setShowPicker(false);
  };

  const hasSelected = !!range[0].startDate && !!range[0].endDate;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowPicker(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      {label && <label className="mb-1 d-block">{label}</label>}

      <button
        type="button"
        onClick={() => setShowPicker(!showPicker)}
        className="btn btn-outline-secondary"
      >
        {hasSelected
          ? `${formatDate(range[0].startDate)} → ${formatDate(
              range[0].endDate,
            )}`
          : "Sélectionner une période"}
      </button>

      {showPicker && (
        <div
          style={{
            position: "absolute",
            zIndex: 100,
            top: "110%",
            background: "white",
            boxShadow: "2px 4px 8px rgba(0,0,0,0.1)",
            borderRadius: 12,
            padding: "10px",
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2 px-2">
            <span className="fw-bold">Choisir une période</span>
            <button
              type="button"
              onClick={handleReset}
              className="btn btn-sm btn-outline-danger"
            >
              Réinitialiser
            </button>
          </div>

          <DateRangePicker
            ranges={range}
            onChange={handleSelect}
            moveRangeOnFirstSelection={false}
            rangeColors={["#FF7722"]}
            locale={fr}
            staticRanges={[...customStaticRanges]}
            inputRanges={[]}
            months={1}
            direction="horizontal"
          />
        </div>
      )}
    </div>
  );
}
